<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Berserker Barbarian Build Guide</h1>
    <p>The Berserker Barbarian is a high-risk, high-reward melee build that relies on the Berserk skill to deal massive magic damage while sacrificing defense. This build is excellent for boss killing and high single-target damage but requires careful play due to its vulnerability.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-berserker-barb.png">
    </div>
    
    <h2>Recommended Gear</h2>
    <Equipments
      class="right"
      :left-weapon="weapon"
      :right-weapon="shield"
      :helm="helm"
      :armor="armor"
      :gloves="gloves"
      :belt="belt"
      :boots="boots"
    />
    <h3>Weapon</h3>
    <ul>
        <li><strong>Grief</strong> (Best damage output for Berserk)</li>
        <li><strong>Death</strong> (High physical damage alternative)</li>
    </ul>
    
    <h3>Shield</h3>
    <ul>
        <li><strong>Phoenix</strong> (Redemption Aura provides survivability)</li>
        <li><strong>Stormshield</strong> (Excellent damage reduction and survivability)</li>
    </ul>
    
    <h3>Helmet</h3>
    <ul>
        <li><strong>Arreat's Face</strong> (Best choice for skills, life leech, and resistances)</li>
    </ul>
    
    <h3>Armor</h3>
    <ul>
        <li><strong>Enigma</strong> (Teleport for mobility)</li>
        <li><strong>Chains of Honor</strong> (Resistances and life leech)</li>
    </ul>
    
    <h3>Gloves</h3>
    <ul>
        <li><strong>Steelrend</strong> (Increases damage)</li>
        <li><strong>Laying of Hands</strong> – Increases attack speed. Increases damage against demons.</li>
    </ul>
    
    <h3>Belt</h3>
    <ul>
        <li><strong>Verdungo’s Hearty Cord</strong> (High survivability)</li>
    </ul>

    <h3>Boots</h3>
    <ul>
        <li><strong>Gore Rider</strong> (Crushing blow and deadly strike)</li>
    </ul>
    
    <h3>Rings</h3>
    <ul>
        <li><strong>Raven Frost</strong> (Cannot be frozen, attack rating boost)</li>
        <li><strong>Bul-Kathos' Wedding Band</strong> (Life leech and skills)</li>
    </ul>
    
    <h3>Amulet</h3>
    <ul>
        <li><strong>Highlord’s Wrath</strong> (Deadly strike and attack speed)</li>
    </ul>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <h2>Core Skill Allocation</h2>
    <div class="m-3 skills-tab right">
      <b-tabs small content-class="m-2" class="tab-text">
        <b-tab title="Warcries">
          <img class="w-100" src="@/assets/builds/skills-berserker-barb-1.png">
        </b-tab>
        <b-tab title="Combat" active>
          <img class="w-100" src="@/assets/builds/skills-berserker-barb-2.png">
        </b-tab>
        <b-tab title="Masteries">
          <img class="w-100" src="@/assets/builds/skills-berserker-barb-3.png">
        </b-tab>
      </b-tabs>
    </div>
    <ul>
        <li><strong>Berserk</strong> - 20 points (Primary damage skill)</li>
        <li><strong>Howl</strong> - 20 points (Berserk synergy, crowd control)</li>
        <li><strong>Blade Mastery</strong> - 20 points (Increases damage)</li>
        <li><strong>Battle Orders</strong> - 20 points (Massive life and mana boost)</li>
        <li><strong>War Cry</strong> - 1 point (Useful for stunning enemies)</li>
        <li><strong>Battle Command</strong> - 1 point (Adds +1 to all skills)</li>
        <li><strong>Increased Speed</strong> - 1 point (Mobility boost)</li>
        <li><strong>Natural Resistance</strong> - 1 or more points (Boosts resistances)</li>
    </ul>
    
    <h2>Attribute Allocation</h2>
    <ul>
        <li><strong>Strength</strong>: Enough for gear</li>
        <li><strong>Dexterity</strong>: Enough for max block if using a shield</li>
        <li><strong>Vitality</strong>: As much as possible</li>
        <li><strong>Energy</strong>: None</li>
    </ul>
    
    <h2>Mercenary Choice</h2>
    <p>Act 2 Might Aura Mercenary (Enhances physical damage)</p>
    <ul>
        <li><strong>Weapon</strong>: Reaper's Toll (Decrepify for damage boost and crowd control)</li>
        <li><strong>Helmet</strong>: Andariel’s Visage (Life leech and attack speed)</li>
        <li><strong>Armor</strong>: Fortitude (Damage boost and resistances)</li>
    </ul>
    
    <h2>Combat Strategy</h2>
    <ol>
        <li><strong>Buff Up</strong>: Use Battle Command, Battle Orders, and Shout before engaging.</li>
        <li><strong>Howl</strong>: Scare away weaker enemies to isolate tougher ones.</li>
        <li><strong>Engage with Berserk</strong>: Deal massive magic damage but be mindful of losing defense.</li>
        <li><strong>Use War Cry</strong>: Stun groups of enemies when needed.</li>
        <li><strong>Positioning</strong>: Utilize Enigma teleport or Increased Speed to move efficiently.</li>
    </ol>
    
    <p>The Berserker Barbarian excels at high single-target damage, making it an excellent boss killer. However, its lack of defense during Berserk requires strategic play and careful positioning.</p>

  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'BerserkerBarb',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-crystal-sword',
        'width': 80,
        'title': 'Grief Runeword',
        'runes': 'Eth + Tir + Lo + Mal + Ral',
        'detail': [
          '35% Chance To Cast Level 15 Venom On Striking',
          '+30-40% Increased Attack Speed',
          'Damage +340-400',
          "Ignore Target's Defense",
          '-25% Target Defense',
          '+1.875-185.625% Damage To Demons (Based on Character Level)',
          'Adds 5-30 Fire Damage',
          '-20-25% To Enemy Poison Resistance',
          '20% Deadly Strike',
          "Prevent Monster Heal",
          '+2 To Mana After Each Kill',
          '+10-15 Life After Each Kill',
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-monarch',
        'width': 80,
        'title': 'Phoenix Runeword',
        'runes': 'Vex + Vex + Lo + Jah',
        'detail': [
          '100% Chance To Cast level 40 Blaze When You Level-up', 
          '40% Chance To Cast Level 22 Firestorm On Striking',
          'Level 10-15 Redemption Aura When Equipped',
          '+350-400 Defense Vs. Missile', 
          '+350-400% Enhanced Damage',
          '-28% To Enemy Fire Resistance',
          '+50 To Life',
          '+5% To Maximum Lightning Resist',
          '+10% To Maximum Fire Resist',
          '+15-21 Fire Absorb',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-avenger-guard',
        'width': 75,
        'title': "Arreat's Face",
        'detail': [
          ' Defense: 302-363', 
          '+150-200% Enhanced Defense',
          '30% Faster Hit Recovery',
          '20% Bonus To Attack Rating', 
          '+2 To Barbarian Skill Levels',
          '3-6% Life Stolen Per Hit',
          'All Resistances +30',
          '+20 To Strength',
          '+20 To Dexterity',
          '+2 To Combat Skills (Barbarian Only)',
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-steelrend',
        'width': 70,
        'title': "Steelrend",
        'detail': [
          'Defense: 232-281', 
          '+170-210 Defense',
          '+30-60% Enhanced Damage',
          '10% Chance of Crushing Blow', 
          '+15-20 To Strength',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-verdungos-hearty-cord',
        'width': 75,
        'title': "Verdungo's Hearty Cord",
        'detail': [
          'Defense: 125-158', 
          '+90-140% Enhanced Defense',
          '10% Faster Hit Recovery',
          '+30-40 To Vitality', 
          'Replenish Life +10-13',
          '+100-120 Maximum Stamina',
          'Damage Reduced By 10-15%',
        ],
      }
    },

    boots () {
      return {
        'img': 'eq-plate-boots',
        'width': 70,
        'title': 'Gore Rider',
        'detail': [
          'Defense: 140-162', 
          '+160-200% Enhanced Defense',
          '30% Faster Run/Walk',
          '10% Chance Of Open Wounds', 
          '15% Chance Of Crushing Blow',
          '15% Deadly Strike',
          'Requirements -25%',
          '+20 Maximum Stamina',
        ],
      }
    },
  }
}
</script>
